import { ActionIcon, Text, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";

dayjs.extend(utc);
dayjs.extend(customParseFormat);
import { ITimelineItemBase } from "src/components/shared/TimelineCalendar/timeline.types";
import { useTheme } from "src/hooks";
import { Flex, StyledTd, StyledTh, StyledTr } from "src/styled";
import { v4 } from "uuid";

import { getCurrentTimeHHMM } from "./utils";
import VitalsInputTimeCell from "./VitalsInputTimeCell";
interface Props {
  onChange: ((items: ITimelineItemBase[]) => void) | undefined;
  onDeleteItem: ((id: string) => void) | undefined;
  items: ITimelineItemBase[];
  subResource?: string;
  suffix?: string;
  date: string;
  disabled?: boolean;
}

const VitalsInputRow = ({
  items: unfilteredItems,
  onChange,
  subResource,
  suffix,
  onDeleteItem,
  date,
  disabled,
}: Props) => {
  const theme = useTheme();
  const flexRef = useRef<HTMLDivElement>(null);
  const [flexWidth, setFlexWidth] = useState<number | undefined>(400); // Default width

  const items = useMemo(
    () => unfilteredItems.filter(item => item.subResource === subResource),
    [unfilteredItems, subResource],
  );

  useEffect(() => {
    const calculateWidth = () => {
      if (flexRef.current) {
        const td = flexRef.current.closest("td");
        if (!td) return;
        const tdWidth = td.getBoundingClientRect().width;
        setFlexWidth(tdWidth - 20); // Set Flex width to td width - 100px
      }
    };
    calculateWidth();
    // window.addEventListener("resize", calculateWidth);
  }, []);

  const handleAddItem = () => {
    const currentTime = getCurrentTimeHHMM();
    const lastTime =
      items.length > 0
        ? dayjs
            .utc(items[items.length - 1]?.end)
            .add(1, "minute")
            .format("HH:mm")
        : currentTime;
    const newEndTime = dayjs
      .utc(lastTime, "HH:mm")
      .add(10, "minutes")
      .format("HH:mm");

    const newItems = [
      ...items,
      {
        id: v4(),
        start: `${date}T${lastTime}:00Z`,
        end: `${date}T${newEndTime}:00Z`,
        title: "",
        subResource,
      },
    ];
    onChange?.(newItems);
    setTimeout(() => {
      if (flexRef.current) {
        flexRef.current.scrollLeft = flexRef.current.scrollWidth;
      }
    }, 0);
  };

  const handleChangeTime = (
    id: string,
    value: string,
    timeProperty: "start" | "end",
  ) => {
    const newItems = items.map(item =>
      item.id === id
        ? {
            ...item,
            [timeProperty]: `${date}T${value}:00Z`,
          }
        : item,
    );
    onChange?.(newItems);
  };

  const handleChangeValue = (id: string, value: string) => {
    const newItems = items.map(item =>
      item.id === id ? { ...item, title: value } : item,
    );
    onChange?.(newItems);
  };

  const type = useMemo(
    () => subResource ?? suffix ?? "Value",
    [subResource, suffix],
  );

  return (
    <Flex
      ref={flexRef}
      align="center"
      gap={5}
      style={{
        overflowX: "scroll",
        overflowY: "hidden",
        width: flexWidth ?? "auto", // Set dynamically calculated width
      }}>
      <table>
        <tbody>
          <StyledTr>
            <StyledTh>Time</StyledTh>
            {items.map(item => (
              <VitalsInputTimeCell
                key={item.id}
                item={item}
                onChangeTime={handleChangeTime}
                disabled={disabled}
              />
            ))}
          </StyledTr>
          <StyledTr>
            <StyledTh>{type}</StyledTh>
            {items.map(item => (
              <StyledTd key={item.id} style={{ padding: 5 }}>
                {disabled ? (
                  <Text>{item.title}</Text>
                ) : (
                  <TextInput
                    value={item.title}
                    w={136}
                    onChange={e => handleChangeValue(item.id, e.target.value)}
                  />
                )}
              </StyledTd>
            ))}
          </StyledTr>
          {!disabled && (
            <tr>
              <td></td>
              {items.map(item => (
                <td key={item.id}>
                  <ActionIcon
                    onClick={() => onDeleteItem?.(item.id)}
                    color="red"
                    style={{ margin: "0 auto" }}>
                    <IconTrash />
                  </ActionIcon>
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
      {!disabled && (
        <ActionIcon
          onClick={handleAddItem}
          mb={28}
          style={{ color: theme.colors.main[9] }}>
          <IconPlus />
        </ActionIcon>
      )}
    </Flex>
  );
};

export default VitalsInputRow;
